
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { FileText, X, Calendar, AlertCircle } from 'lucide-react';

export const ExpiringPrescriptionsNotification: React.FC = () => {
  const { customers } = useData();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  const expiredPrescriptions = customers
    .filter(c => c.prescriptions && c.prescriptions.length > 0)
    .map(c => {
      const sorted = [...c.prescriptions].sort((a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      const latest = sorted[0];
      const rxDate = new Date(latest.date);
      return {
        customer: c,
        prescription: latest,
        isExpired: rxDate < oneYearAgo,
        daysSince: Math.floor((today.getTime() - rxDate.getTime()) / (1000 * 60 * 60 * 24)),
      };
    })
    .filter(item => item.isExpired)
    .sort((a, b) => b.daysSince - a.daysSince);

  if (expiredPrescriptions.length === 0) return null;

  return (
    <div className="relative z-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full text-red-500 transition-all duration-300 group animate-glow-red hover:scale-105"
        title={`${expiredPrescriptions.length} receita(s) vencida(s)`}
      >
        <FileText className="w-6 h-6" />
        <span className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-red-500/15 border border-red-500/30 text-[10px] font-bold text-red-700 dark:text-red-400 shadow-sm ring-2 ring-white dark:ring-slate-800">
          {expiredPrescriptions.length}
        </span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-2 text-slate-900 dark:text-slate-50">
                <AlertCircle className="w-5 h-5" />
                <span className="font-bold">Receitas Vencidas</span>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-50">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="max-h-[28rem] overflow-y-auto py-2">
              {expiredPrescriptions.map(({ customer, prescription, daysSince }) => (
                <div key={customer.id} className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 last:border-0 hover:bg-slate-100 dark:hover:bg-[#26334D] transition-colors">
                  <div
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/customers', { state: { viewCustomerId: customer.id } });
                    }}
                    className="cursor-pointer"
                  >
                    <p className="font-semibold text-slate-900 dark:text-blue-400">{customer.name}</p>
                    <div className="flex items-center text-xs text-slate-600 dark:text-slate-400 gap-2 mt-0.5">
                      <span className="flex items-center gap-1 bg-orange-500/15 border border-orange-500/30 text-orange-700 dark:text-orange-400 px-1.5 py-0.5 rounded">
                        <Calendar className="w-3 h-3" />
                        {new Date(prescription.date).toLocaleDateString('pt-BR')}
                      </span>
                      <span className="text-[10px] text-red-500 dark:text-red-400 font-medium">{daysSince} dias vencida</span>
                    </div>
                    <p className="text-[10px] text-slate-600 dark:text-slate-400 mt-1">Dr(a). {prescription.doctorName}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-50 dark:bg-slate-900 px-4 py-2 text-center text-xs text-slate-600 dark:text-slate-400">
              Total: {expiredPrescriptions.length} receita(s) vencida(s) · Clique para ver cliente
            </div>
          </div>
        </>
      )}
    </div>
  );
};
